/* ── Chatbularis y el panel como apps ─────────────────────────────────
   No son apps de tienda: es la misma página, instalada en la pantalla de
   inicio del teléfono o como programa del PC, con el manifiesto de
   public/chatbularis.webmanifest o public/tabularis.webmanifest. Se abre con
   un toque y a pantalla completa, sin la barra del navegador.

   Cada sistema lo permite a su manera:
   - Android (Chrome, Edge, Samsung): el navegador avisa que se puede instalar
     y deja abrir su propio diálogo desde un botón nuestro.
   - PC con Chrome o Edge: igual que Android, con el mismo botón.
   - iPhone y iPad: Apple no deja ofrecer un botón. Solo se puede explicar el
     camino: Compartir → "Agregar a inicio".
   - Mac con Safari: tampoco hay botón; el camino es Archivo → "Agregar al
     Dock". Firefox en el PC no instala páginas.

   El aviso de Android llega una sola vez, apenas carga la página, y a veces
   antes de que exista la pantalla del chat. Por eso se escucha acá, al cargar
   este módulo, y se guarda para cuando el botón lo pida. */

type AvisoInstalacion = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

let aviso: AvisoInstalacion | null = null;
const oyentes = new Set<() => void>();
const avisar = () => oyentes.forEach(f => f());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Sin esto Chrome muestra su propia barra, que aparece en cualquier momento
    // y tapa el cuadro de texto justo cuando se está escribiendo.
    e.preventDefault();
    aviso = e as AvisoInstalacion;
    avisar();
  });
  window.addEventListener('appinstalled', () => { aviso = null; avisar(); });
}

/** ¿Ya se está usando como app instalada? */
export const esAppInstalada = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;

/** iPhone o iPad. El iPad moderno se presenta como Mac, y lo que lo delata es
 *  la pantalla táctil. */
export const esIOS = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

export const esAndroid = () => /Android/.test(navigator.userAgent);

/** Safari en un Mac (no el iPad, que también dice "Macintosh"). Chrome y Edge
 *  en el Mac se anuncian con su nombre además de "Safari". */
export const esSafariMac = () =>
  /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints <= 1 &&
  /Safari/.test(navigator.userAgent) && !/Chrome|Chromium|CriOS|Edg|Firefox|OPR/.test(navigator.userAgent);

/** ¿El navegador dejó ofrecer la instalación con un botón? */
export const puedeInstalarConBoton = () => aviso !== null;

/** Abre el diálogo de instalación del navegador. Devuelve true si la persona
 *  aceptó. El aviso sirve una sola vez: después hay que esperar otro. */
export async function instalar(): Promise<boolean> {
  if (!aviso) return false;
  const a = aviso;
  aviso = null;
  await a.prompt();
  const { outcome } = await a.userChoice;
  avisar();
  return outcome === 'accepted';
}

/** Para que la pantalla se entere cuando el navegador permite instalar. */
export function alCambiarInstalacion(f: () => void): () => void {
  oyentes.add(f);
  return () => { oyentes.delete(f); };
}
